// Admin Hub — a calm, SharePoint-like governance surface. Strictly content +
// users management: list, edit, publish, spot broken links, assign roles. No
// gamification, no AI generation. Visibility is gated here for UX; the real
// enforcement is the PocketBase API rules + the role-change hook.
import {
  isEditor,
  isAdmin,
  currentRole,
  listAllArticles,
  getArticle,
  saveArticle,
  deleteArticle,
  listUsers,
  setUserRole,
} from "../cms/api.js";
import { mountEditor } from "./editor.js";
import { escapeHtml } from "../ui/helpers.js";

const NAV_GROUPS = ["", "Word classes", "Other", "Essentials"];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function statusBadge(s) {
  // Legacy articles have an empty status and are publicly visible (rule is
  // status != "draft"), so anything not explicitly "draft" reads as Published.
  return s === "draft"
    ? `<span class="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Draft</span>`
    : `<span class="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Published</span>`;
}

// Find broken internal links across the whole article set.
function brokenLinks(articles) {
  const slugs = new Set(articles.map((a) => a.slug));
  const out = [];
  const re = /\]\(article:([a-z0-9-]+)\)/g;
  for (const a of articles) {
    const md = a.content_markdown || "";
    let m;
    const seen = new Set();
    while ((m = re.exec(md)) !== null) {
      const target = m[1];
      if (!slugs.has(target) && !seen.has(target)) {
        seen.add(target);
        out.push({ source: a.title || a.slug, sourceId: a.id, target });
      }
    }
  }
  return out;
}

export async function adminView(app) {
  if (!isEditor()) {
    app.innerHTML = `
      <section class="max-w-xl mx-auto py-16 text-center">
        <p class="text-4xl mb-3">🔒</p>
        <h2 class="text-2xl font-bold text-sage-800">Admin Hub</h2>
        <p class="text-sage-600 text-sm mt-2">This area is for editors and admins. Ask an admin if you need access.</p>
      </section>`;
    return;
  }
  await renderList(app);
}

async function renderList(app) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading content…</div>`;
  const articles = await listAllArticles();
  const broken = brokenLinks(articles);

  const rowHTML = (a) => `
        <tr class="border-b border-sage-50 last:border-0">
          <td class="py-2 pr-3"><button data-edit="${a.id}" class="font-medium text-sage-800 hover:text-sage-600 text-left">${escapeHtml(a.title || "Untitled")}</button>
            <span class="block text-xs text-sage-400">${escapeHtml(a.slug || "")}</span></td>
          <td class="py-2 px-3">${statusBadge(a.status)}</td>
          <td class="py-2 px-3 text-xs text-sage-500">${a.is_navigable ? "in nav" : ""}</td>
          <td class="py-2 pl-3 text-right"><button data-edit="${a.id}" class="text-xs font-medium text-sage-600 hover:text-sage-800">Edit</button></td>
        </tr>`;

  app.innerHTML = `
    <section class="max-w-4xl mx-auto fade-in">
      <div class="flex items-center justify-between gap-3 mb-5">
        <div>
          <h2 class="text-2xl font-bold text-sage-800">Admin Hub</h2>
          <p class="text-sage-600 text-sm mt-1">Manage articles, publishing, and links. Signed in as <b>${escapeHtml(currentRole())}</b>.</p>
        </div>
        <div class="flex items-center gap-2">
          ${isAdmin() ? `<button id="cms-users" class="text-sm font-medium text-sage-600 hover:text-sage-800 px-3 py-2">Users</button>` : ""}
          <button id="cms-new" class="bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">New article</button>
        </div>
      </div>

      ${
        broken.length
          ? `<div class="mb-4 rounded-xl border border-rose-200 bg-rose-50/70 p-3">
               <p class="text-sm font-semibold text-rose-700 mb-1">${broken.length} broken internal ${broken.length === 1 ? "link" : "links"}</p>
               <ul class="text-xs text-rose-700/90 list-disc pl-5 space-y-0.5">
                 ${broken.map((b) => `<li>${escapeHtml(b.source)} → <code>article:${escapeHtml(b.target)}</code> (no such article)</li>`).join("")}
               </ul>
             </div>`
          : ""
      }

      <div class="bg-white rounded-2xl border border-sage-100 p-4">
        <input id="cms-search" type="search" placeholder="Search articles by title or slug…" class="w-full mb-3 px-3 py-2 border border-sage-200 rounded-lg text-sm focus:border-sage-400 outline-none" />
        <table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Title</th><th class="py-2 px-3">Status</th><th class="py-2 px-3">Nav</th><th class="py-2 pl-3"></th>
          </tr></thead>
          <tbody id="cms-rows"></tbody>
        </table>
      </div>
    </section>`;

  const tbody = app.querySelector("#cms-rows");
  const wireRows = () =>
    tbody.querySelectorAll("[data-edit]").forEach((b) =>
      b.addEventListener("click", async () => {
        const a = await getArticle(b.dataset.edit);
        if (a) renderEdit(app, a);
      })
    );
  const paint = (q) => {
    const query = (q || "").trim().toLowerCase();
    const list = query
      ? articles.filter((a) => (a.title || "").toLowerCase().includes(query) || (a.slug || "").toLowerCase().includes(query))
      : articles;
    tbody.innerHTML = list.length
      ? list.map(rowHTML).join("")
      : `<tr><td colspan="4" class="py-8 text-center text-sage-500">${query ? "No matches." : "No articles yet — create your first."}</td></tr>`;
    wireRows();
  };
  paint("");
  app.querySelector("#cms-search").addEventListener("input", (e) => paint(e.target.value));

  app.querySelector("#cms-new").addEventListener("click", () => renderEdit(app, null));
  const usersBtn = app.querySelector("#cms-users");
  if (usersBtn) usersBtn.addEventListener("click", () => renderUsers(app));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function renderEdit(app, article) {
  const isNew = !article;
  const a = article || {};
  const groupOpts = NAV_GROUPS.map(
    (g) => `<option value="${escapeHtml(g)}"${(a.nav_group || "") === g ? " selected" : ""}>${g || "— none —"}</option>`
  ).join("");
  const pathOpts = ["", "A1", "A2", "B1", "B2", "C1", "C2"]
    .map((p) => `<option value="${p}"${(a.difficulty_level || "") === p ? " selected" : ""}>${p || "— none —"}</option>`)
    .join("");

  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <button id="cms-back" class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← All content</button>
      <div class="bg-white rounded-2xl border border-sage-100 p-5 sm:p-6 space-y-4">
        <input id="f-title" type="text" maxlength="300" placeholder="Article title"
          class="w-full text-xl font-bold text-sage-800 border-0 border-b border-sage-100 focus:border-sage-400 outline-none pb-2 placeholder:text-sage-300"
          value="${escapeHtml(a.title || "")}" />

        <div class="flex flex-wrap gap-3 items-end text-sm">
          <label class="text-sage-700">Slug
            <input id="f-slug" type="text" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 bg-white" value="${escapeHtml(a.slug || "")}" placeholder="auto from title" />
          </label>
          <label class="text-sage-700">Status
            <select id="f-status" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="draft"${a.status === "draft" ? " selected" : ""}>Draft</option>
              <option value="published"${a.status !== "draft" ? " selected" : ""}>Published</option>
            </select>
          </label>
          <label class="text-sage-700 flex items-center gap-2 mt-5">
            <input id="f-nav" type="checkbox"${a.is_navigable ? " checked" : ""} /> In navigation
          </label>
          <label class="text-sage-700">Nav group
            <select id="f-navgroup" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 bg-white">${groupOpts}</select>
          </label>
          <label class="text-sage-700">Order
            <input id="f-navorder" type="number" class="mt-1 block w-20 border border-sage-200 rounded-lg px-2 py-1.5 bg-white" value="${Number(a.nav_order) || 0}" />
          </label>
        </div>

        <div class="flex flex-wrap gap-3 items-end text-sm">
          <label class="text-sage-700">Learning path (dashboard)
            <select id="f-path" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 bg-white">${pathOpts}</select>
          </label>
          <label class="text-sage-700">Path order
            <input id="f-pathorder" type="number" class="mt-1 block w-20 border border-sage-200 rounded-lg px-2 py-1.5 bg-white" value="${Number(a.path_order) || 0}" />
          </label>
        </div>

        <label class="block text-sm text-sage-700">Aliases (comma-separated — terms that auto-link here)
          <input id="f-aliases" type="text" class="mt-1 block w-full border border-sage-200 rounded-lg px-2 py-1.5 bg-white" value="${escapeHtml(a.aliases || "")}" placeholder="e.g. subjunctive, mood" />
        </label>
        <label class="block text-sm text-sage-700">Share description
          <input id="f-meta" type="text" maxlength="300" class="mt-1 block w-full border border-sage-200 rounded-lg px-2 py-1.5 bg-white" value="${escapeHtml(a.meta_description || "")}" />
        </label>

        <div id="cms-editor"><p class="text-sage-400 text-sm py-8 text-center">Loading editor…</p></div>

        <div class="flex flex-wrap gap-2 pt-2">
          <button id="cms-save" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2 rounded-lg text-sm font-medium">Save</button>
          <button id="cms-cancel" class="bg-white text-sage-600 border border-sage-200 hover:border-sage-300 px-4 py-2 rounded-lg text-sm font-medium">Cancel</button>
          ${!isNew ? `<button id="cms-delete" class="ml-auto text-rose-600 hover:text-rose-700 px-3 py-2 rounded-lg text-sm font-medium">Delete</button>` : ""}
        </div>
        <p id="cms-msg" class="text-sm text-rose-600"></p>
      </div>
    </section>`;

  let editor = null;
  mountEditor(app.querySelector("#cms-editor"), a.content_markdown || "", (msg) => {
    const m = app.querySelector("#cms-msg");
    if (m) m.textContent = msg;
  }).then((ed) => {
    editor = ed;
  });

  // Auto-fill slug from title for new articles until the user edits it.
  const titleEl = app.querySelector("#f-title");
  const slugEl = app.querySelector("#f-slug");
  let slugTouched = !isNew;
  slugEl.addEventListener("input", () => {
    slugTouched = true;
  });
  titleEl.addEventListener("input", () => {
    if (!slugTouched) slugEl.value = slugify(titleEl.value);
  });

  const leave = () => {
    if (editor) {
      try {
        editor.destroy();
      } catch {
        /* ignore */
      }
    }
    renderList(app);
  };

  app.querySelector("#cms-back").addEventListener("click", leave);
  app.querySelector("#cms-cancel").addEventListener("click", leave);

  app.querySelector("#cms-save").addEventListener("click", async () => {
    const msg = app.querySelector("#cms-msg");
    const title = titleEl.value.trim();
    const slug = (slugEl.value.trim() || slugify(title));
    if (!title || !slug) {
      msg.textContent = "Title and slug are required.";
      return;
    }
    const btn = app.querySelector("#cms-save");
    btn.disabled = true;
    btn.textContent = "Saving…";
    try {
      await saveArticle(isNew ? null : a.id, {
        title,
        slug,
        content_markdown: editor ? editor.getMarkdown() : a.content_markdown || "",
        status: app.querySelector("#f-status").value,
        is_navigable: app.querySelector("#f-nav").checked,
        nav_group: app.querySelector("#f-navgroup").value,
        nav_order: app.querySelector("#f-navorder").value,
        aliases: app.querySelector("#f-aliases").value,
        meta_description: app.querySelector("#f-meta").value,
        difficulty_level: app.querySelector("#f-path").value,
        path_order: app.querySelector("#f-pathorder").value,
      });
      leave();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = "Save";
      msg.textContent =
        err && /slug/i.test(String(err && err.message)) ? "That slug is already taken." : "Couldn't save — check the fields and try again.";
    }
  });

  const delBtn = app.querySelector("#cms-delete");
  if (delBtn)
    delBtn.addEventListener("click", async () => {
      if (!confirm("Delete this article? This can't be undone.")) return;
      await deleteArticle(a.id);
      leave();
    });

  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function renderUsers(app) {
  if (!isAdmin()) {
    renderList(app);
    return;
  }
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading users…</div>`;
  const users = await listUsers();

  const rowHTML = (u) => `
      <tr class="border-b border-sage-50 last:border-0">
        <td class="py-2 pr-3">
          <span class="block font-medium text-sage-800">${escapeHtml(u.name || u.email || "—")}</span>
          <span class="block text-xs text-sage-400">${escapeHtml(u.email || "")}</span>
        </td>
        <td class="py-2 pl-3 text-right">
          <select data-role-for="${u.id}" class="border border-sage-200 rounded-lg px-2 py-1 text-sm bg-white">
            ${["member", "editor", "admin"].map((r) => `<option value="${r}"${(u.role || "member") === r ? " selected" : ""}>${r}</option>`).join("")}
          </select>
        </td>
      </tr>`;

  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <button id="cms-back" class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← Admin Hub</button>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Users & roles</h2>
      <p class="text-sage-600 text-sm mb-5">Members read. Editors manage content. Admins also manage users.</p>
      <div class="bg-white rounded-2xl border border-sage-100 p-4">
        <input id="cms-usearch" type="search" placeholder="Search users by name or email…" class="w-full mb-3 px-3 py-2 border border-sage-200 rounded-lg text-sm focus:border-sage-400 outline-none" />
        <table class="w-full text-sm"><tbody id="cms-urows"></tbody></table>
      </div>
      <p id="cms-umsg" class="text-sm text-sage-500 mt-3"></p>
    </section>`;

  const tbody = app.querySelector("#cms-urows");
  const wireRoles = () =>
    tbody.querySelectorAll("[data-role-for]").forEach((sel) =>
      sel.addEventListener("change", async () => {
        const msg = app.querySelector("#cms-umsg");
        const prev = sel.dataset.prev || sel.value;
        try {
          await setUserRole(sel.dataset.roleFor, sel.value);
          msg.textContent = "Role updated.";
          sel.dataset.prev = sel.value;
        } catch {
          sel.value = prev;
          msg.textContent = "Couldn't change that role.";
        }
      })
    );
  const paint = (q) => {
    const query = (q || "").trim().toLowerCase();
    const list = query
      ? users.filter((u) => (u.name || "").toLowerCase().includes(query) || (u.email || "").toLowerCase().includes(query))
      : users;
    tbody.innerHTML = list.length
      ? list.map(rowHTML).join("")
      : `<tr><td class="py-8 text-center text-sage-500">${query ? "No matches." : "No users found."}</td></tr>`;
    wireRoles();
  };
  paint("");
  app.querySelector("#cms-usearch").addEventListener("input", (e) => paint(e.target.value));
  app.querySelector("#cms-back").addEventListener("click", () => renderList(app));
  window.scrollTo({ top: 0, behavior: "smooth" });
}
